/* eslint-disable react-hooks/exhaustive-deps -- effects intentionally run on mount / on the listed deps; adding the flagged functions risks render loops. */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Typography,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  AvatarGroup,
  Rating,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { outfitsAPI, wardrobeAPI } from '../services/api';
import { useUI } from '../contexts/UIContext';
import { Outfit, WardrobeItem } from '../types';

const Outfits: React.FC = () => {
  const { toast, confirm } = useUI();
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [wardrobeItems, setWardrobeItems] = useState<WardrobeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedOutfit, setSelectedOutfit] = useState<Outfit | null>(null);
  
  // Form fields
  const [outfitName, setOutfitName] = useState('');
  const [outfitDescription, setOutfitDescription] = useState('');
  const [outfitOccasion, setOutfitOccasion] = useState('');
  const [outfitSeason, setOutfitSeason] = useState('');
  const [outfitStyle, setOutfitStyle] = useState('');
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [outfitRating, setOutfitRating] = useState(0);
  const [thumbnailItemId, setThumbnailItemId] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [outfitsRes, itemsRes] = await Promise.all([
        outfitsAPI.getOutfits(),
        wardrobeAPI.getItems(),
      ]);
      setOutfits(outfitsRes.data);
      setWardrobeItems(itemsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOutfit = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const handleEditOutfit = (outfit: Outfit) => {
    setSelectedOutfit(outfit);
    setOutfitName(outfit.name);
    setOutfitDescription(outfit.description || '');
    setOutfitOccasion(outfit.occasion || '');
    setOutfitSeason(outfit.season || '');
    setOutfitStyle(outfit.style || '');
    setOutfitRating(outfit.rating || 0);
    setSelectedItems(outfit.items.map(item => item.item_id));
    setThumbnailItemId(outfit.thumbnail_item_id || null);
    setCreateDialogOpen(true);
  };

  const handleSaveOutfit = async () => {
    try {
      const outfitData = {
        name: outfitName,
        description: outfitDescription,
        occasion: outfitOccasion,
        season: outfitSeason,
        style: outfitStyle,
        rating: outfitRating,
        item_ids: selectedItems,
        thumbnail_item_id: thumbnailItemId,
      };

      if (selectedOutfit) {
        await outfitsAPI.updateOutfit(selectedOutfit.id, outfitData);
      } else {
        await outfitsAPI.createOutfit(outfitData);
      }

      fetchData();
      setCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving outfit:', error);
    }
  };

  const handleDeleteOutfit = async (outfitId: number) => {
    const ok = await confirm({
      title: 'Delete this outfit?',
      message: 'The look will be removed from your collection. Items inside it stay in your wardrobe.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await outfitsAPI.deleteOutfit(outfitId);
      fetchData();
      toast('Outfit deleted', 'success');
    } catch (error) {
      console.error('Error deleting outfit:', error);
      toast('Failed to delete outfit', 'error');
    }
  };

  const handleToggleFavorite = async (outfit: Outfit) => {
    try {
      await outfitsAPI.updateOutfit(outfit.id, { is_favorite: !outfit.is_favorite });
      fetchData();
    } catch (error) {
      console.error('Error updating favorite status:', error);
    }
  };

  const handleWearOutfit = async (outfitId: number) => {
    try {
      await outfitsAPI.recordWear(outfitId);
      fetchData();
    } catch (error) {
      console.error('Error recording outfit wear:', error);
    }
  };

  // "worn today" = last_worn matches today's (UTC) date, matching the backend stamp
  const todayISO = new Date().toISOString().slice(0, 10);
  const isWornToday = (lastWorn?: string | null) => !!lastWorn && String(lastWorn).slice(0, 10) === todayISO;

  const resetForm = () => {
    setSelectedOutfit(null);
    setOutfitName('');
    setOutfitDescription('');
    setOutfitOccasion('');
    setOutfitSeason('');
    setOutfitStyle('');
    setOutfitRating(0);
    setSelectedItems([]);
    setThumbnailItemId(null);
  };

  const toggleItemSelection = (itemId: number) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Auto-select thumbnail based on priority: full view > upper body (shirt, jacket, etc)
  const autoSelectThumbnail = (itemIds: number[]) => {
    if (itemIds.length === 0) {
      setThumbnailItemId(null);
      return;
    }

    const selectedWardrobeItems = wardrobeItems.filter(item => itemIds.includes(item.id));

    // Priority 1: Look for full view items (dresses, jumpsuits, full outfits)
    const fullViewCategories = ['dress', 'jumpsuit', 'romper', 'overall'];
    const fullViewItem = selectedWardrobeItems.find(item =>
      fullViewCategories.some(cat => item.category?.toLowerCase().includes(cat))
    );

    if (fullViewItem) {
      setThumbnailItemId(fullViewItem.id);
      return;
    }

    // Priority 2: Look for upper body items (shirts, jackets, sweaters, etc)
    const upperBodyCategories = ['shirt', 'blouse', 'top', 'jacket', 'blazer', 'sweater', 'hoodie', 'cardigan', 't-shirt', 'polo'];
    const upperBodyItem = selectedWardrobeItems.find(item =>
      upperBodyCategories.some(cat => item.category?.toLowerCase().includes(cat))
    );

    if (upperBodyItem) {
      setThumbnailItemId(upperBodyItem.id);
      return;
    }

    // Priority 3: Default to first selected item
    setThumbnailItemId(itemIds[0]);
  };

  // Auto-select thumbnail when items change (only for new outfits)
  useEffect(() => {
    if (!selectedOutfit && selectedItems.length > 0 && !thumbnailItemId) {
      autoSelectThumbnail(selectedItems);
    }
  }, [selectedItems, selectedOutfit, wardrobeItems]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box className="fade-in-up" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
            {outfits.length} curated looks
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5 }}>My Outfits</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateOutfit}
        >
          Create Outfit
        </Button>
      </Box>

      {outfits.length === 0 && !loading ? (
        <Box className="fade-in" sx={{ textAlign: 'center', py: 8, border: '1px dashed rgba(0,0,0,0.15)' }}>
          <Typography variant="overline" color="text.secondary">Nothing styled yet</Typography>
          <Typography variant="h6" sx={{ mt: 1, mb: 1.5 }}>Put together your first look</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
            Combine pieces from your wardrobe into named outfits you can wear, rate, and recall.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOutfit}>
            Create Your First Outfit
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {outfits.map((outfit, idx) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              key={outfit.id}
              sx={{
                opacity: 0,
                animation: 'fadeInUp 0.5s ease-out forwards',
                animationDelay: `${Math.min(idx, 12) * 0.04}s`,
              }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  '&:hover .outfit-thumb': { transform: 'scale(1.05)' },
                  '&:hover .outfit-actions': { opacity: 1, transform: 'translateY(0)' },
                }}
              >
                {outfit.thumbnail_url && (
                  <Box sx={{ position: 'relative', overflow: 'hidden', cursor: 'pointer' }} onClick={() => handleEditOutfit(outfit)}>
                    <CardMedia
                      component="img"
                      className="outfit-thumb"
                      image={outfit.thumbnail_url}
                      alt={outfit.name}
                      sx={{ height: 220, objectFit: 'cover', transition: 'transform 0.5s cubic-bezier(0.4,0,0.2,1)' }}
                    />
                    {outfit.is_favorite && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: 'rgba(196, 30, 58, 0.95)',
                          color: '#fff',
                          px: 0.75,
                          py: 0.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        <FavoriteIcon sx={{ fontSize: 12 }} /> Favorite
                      </Box>
                    )}
                    <Box
                      className="outfit-actions"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)',
                        opacity: 0,
                        transform: 'translateY(8px)',
                        transition: 'opacity 0.3s ease, transform 0.3s ease',
                        display: 'flex',
                        gap: 0.5,
                        p: 1,
                        justifyContent: 'flex-end',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleToggleFavorite(outfit)}>
                        {outfit.is_favorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleEditOutfit(outfit)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleDeleteOutfit(outfit.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="div" sx={{ fontSize: '1rem', lineHeight: 1.3 }}>
                    {outfit.name}
                  </Typography>
                  {outfit.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {outfit.description}
                    </Typography>
                  )}

                  <Box sx={{ mt: 2, mb: 1 }}>
                    <AvatarGroup max={4}>
                      {outfit.items.map((item) => (
                        <Avatar
                          key={item.id}
                          src={item.item?.image_url}
                          alt={item.item?.name}
                        />
                      ))}
                    </AvatarGroup>
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    {outfit.occasion && (
                      <Chip label={outfit.occasion} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                    {outfit.season && (
                      <Chip label={outfit.season} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                    {outfit.style && (
                      <Chip label={outfit.style} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                  </Box>

                  {outfit.rating && outfit.rating > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Rating value={outfit.rating} readOnly size="small" />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      Worn {outfit.wear_count} {outfit.wear_count === 1 ? 'time' : 'times'}
                    </Typography>
                    {isWornToday(outfit.last_worn) && (
                      <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                        label="Worn today"
                        sx={{ height: 22 }}
                      />
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <Button
                    size="small"
                    fullWidth
                    color={isWornToday(outfit.last_worn) ? 'success' : 'primary'}
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleWearOutfit(outfit.id)}
                  >
                    {isWornToday(outfit.last_worn) ? 'Worn today' : 'Wear Today'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedOutfit ? 'Edit Outfit' : 'Create New Outfit'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Outfit Name"
                value={outfitName}
                onChange={(e) => setOutfitName(e.target.value)}
                required
              />
            </Grid>
            
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                value={outfitDescription}
                onChange={(e) => setOutfitDescription(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Occasion</InputLabel>
                <Select
                  value={outfitOccasion}
                  label="Occasion"
                  onChange={(e) => setOutfitOccasion(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="casual">Casual</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="formal">Formal</MenuItem>
                  <MenuItem value="party">Party</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="workout">Workout</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Season</InputLabel>
                <Select
                  value={outfitSeason}
                  label="Season"
                  onChange={(e) => setOutfitSeason(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="spring">Spring</MenuItem>
                  <MenuItem value="summer">Summer</MenuItem>
                  <MenuItem value="fall">Fall</MenuItem>
                  <MenuItem value="winter">Winter</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Style</InputLabel>
                <Select
                  value={outfitStyle}
                  label="Style"
                  onChange={(e) => setOutfitStyle(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="casual">Casual</MenuItem>
                  <MenuItem value="formal">Formal</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="streetwear">Streetwear</MenuItem>
                  <MenuItem value="athleisure">Athleisure</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={12}>
              <Typography component="legend">Rating</Typography>
              <Rating
                value={outfitRating}
                onChange={(e, newValue) => setOutfitRating(newValue || 0)}
              />
            </Grid>
            
            <Grid size={12}>
              <Typography variant="subtitle1" gutterBottom>
                Select Items for this Outfit:
              </Typography>
              <Grid container spacing={1}>
                {wardrobeItems.map((item) => (
                  <Grid key={item.id}>
                    <Chip
                      label={item.name}
                      onClick={() => toggleItemSelection(item.id)}
                      color={selectedItems.includes(item.id) ? 'primary' : 'default'}
                      icon={selectedItems.includes(item.id) ? <CheckCircleIcon /> : undefined}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Thumbnail Selector */}
            {selectedItems.length > 0 && (
              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Select Thumbnail Image:
                </Typography>
                <RadioGroup
                  value={thumbnailItemId || ''}
                  onChange={(e) => setThumbnailItemId(Number(e.target.value))}
                >
                  <Grid container spacing={2}>
                    {wardrobeItems
                      .filter(item => selectedItems.includes(item.id))
                      .map((item) => (
                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={item.id}>
                          <Box
                            sx={{
                              position: 'relative',
                              border: thumbnailItemId === item.id ? '3px solid' : '1px solid',
                              borderColor: thumbnailItemId === item.id ? 'primary.main' : 'divider',
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              '&:hover': {
                                borderColor: 'primary.main',
                              },
                            }}
                            onClick={() => setThumbnailItemId(item.id)}
                          >
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={item.name}
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '120px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'grey.200',
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  No Image
                                </Typography>
                              </Box>
                            )}
                            <FormControlLabel
                              value={item.id}
                              control={<Radio />}
                              label={item.name}
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                                m: 0,
                                pl: 1,
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '0.75rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                      ))}
                  </Grid>
                </RadioGroup>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveOutfit}
            variant="contained"
            disabled={!outfitName || selectedItems.length === 0}
          >
            {selectedOutfit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Outfits;